/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useState } from 'react';
import { Formik, Form, FormikHelpers, useFormik, FormikContext } from 'formik';
import { useParams } from 'react-router-dom';
import cn from 'classnames';
import { signInWithPhoneNumber, RecaptchaVerifier, ConfirmationResult } from 'firebase/auth';

import { auth as firebaseAuth } from 'firebase-service/firebase-client';
import { UserLoginType } from 'client/types';
import { signIn } from 'client/redux/client/actions';
import { setListenerHistory } from 'client/redux/room/actions';
import { translateT, useAppDispatch, useAppSelector, useTranslation, useGAEventCommonProps } from 'client/hooks';
import { analytics } from 'client/utils/analytics';
import { FormManager, InitialValues } from 'client/modules/form-module';
import { setFormByUserType } from 'client/redux/login';
import { USER_TYPE } from 'constants/index';
import { selectUserUID, selectUserName } from 'client/redux/client/selectors';

import Input from '../Input';
import Button from '../../Button';
import SMSOTPForm from '../SMSOTPForm';

import css from './Form.module.scss';

type FormProps = {
  userLoginType: UserLoginType;
  translate: translateT;
  isResendedCode: boolean;
};

const RenderFormByUserType: FC<FormProps> = ({ userLoginType, translate, isResendedCode }) => {
  switch (userLoginType) {
    case UserLoginType.regular: {
      return (
        <div className={css.inputs}>
          <Input
            name="nickname"
            label={translate('form.regular.nickname')}
            placeholder={translate('form.regular.placeholder')}
            type="text"
          />
          <Input
            name="agreement"
            label={translate('form.regular.checkbox')}
            placeholder={translate('')}
            type="checkbox"
          />
        </div>
      );
    }
    case UserLoginType.custom: {
      return (
        <div className={css.inputs} data-testid="custom-login-form">
          <Input
            name="email"
            label={translate('form.custom.emailInputLabel')}
            placeholder={translate('form.custom.emailPlaceholder')}
            type="email"
            isEmail
          />
          <Input
            name="phone"
            label={translate('form.custom.phoneInputLabel')}
            placeholder={translate('form.custom.phonePlaceholder')}
            type="tel"
          />
        </div>
      );
    }
    case UserLoginType.sms: {
      return (
        <div className={css.smsForm}>
          <SMSOTPForm isResendedCode={isResendedCode} />
        </div>
      );
    }

    default: {
      return <></>;
    }
  }
};

type Props = {
  userLoginType: UserLoginType;
  onSuccess: () => void;
  isIncorrectCode: boolean;
  setIsIncorrectCode: (state: boolean) => void;
  onJustListenBtnClick: () => void;
};

const LoginForm: FC<Props> = ({
  onSuccess,
  userLoginType,
  isIncorrectCode,
  onJustListenBtnClick,
  setIsIncorrectCode,
}) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isResendedCode, setIsResendedCode] = useState(false);

  const dispatch = useAppDispatch();
  const { common } = useGAEventCommonProps();

  const userId = useAppSelector(selectUserUID);
  const userName = useAppSelector(selectUserName);
  const { translate } = useTranslation('login');
  const routeParams = useParams<{ roomId: string }>();

  const recaptchaVerifier = React.useRef<RecaptchaVerifier>();
  const confirmationResultRef = React.useRef<ConfirmationResult>();
  const FormManagerRef = React.useRef<FormManager>();

  FormManagerRef.current = new FormManager(userLoginType, translate);
  const buttonLabel = FormManagerRef.current.getSubmitButtonLabel();

  const isCustom: boolean = userLoginType === UserLoginType.custom;
  const isRegular: boolean = userLoginType === UserLoginType.regular;
  const isSms: boolean = userLoginType === UserLoginType.sms;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmit = async (values: any, actions: FormikHelpers<any>): Promise<void> => {
    analytics.gtag.event('audio_chat_content_click', {
      ...common,
      text_clicked: buttonLabel,
    });

    if (isCustom && values.phone) {
      await verifyPhone(`+972${values.phone.slice(1)}`);
    }

    if (isSms && values.code) {
      await verifyCode(values.code, values.email, actions.setFieldError);
    }

    if (isRegular) {
      // listener login

      const signInResponse = await dispatch(
        signIn({
          uid: userId,
          name: values.nickname,
          type: USER_TYPE.LISTENER,
          roomId: routeParams.roomId,
        }),
      );

      if (typeof signInResponse.payload === 'string' && signInResponse.payload?.includes('Listener exist')) {
        actions.setFieldError('nickname', translate('form.errors.authErrors.listenerExist'));
      } else {
        onSuccess();
      }
    }
  };

  const formikContext = useFormik({
    //@ts-expect-error I know
    initialValues: InitialValues[userLoginType],
    onSubmit: onSubmit,
    validationSchema: FormManagerRef.current.getValidationSchema(),
    validateOnChange: true,
    validateOnBlur: true,
  });
  const onButtonDisable = FormManagerRef.current.onButtonDisable;
  const isButtonDisabled: boolean = onButtonDisable(formikContext.errors, formikContext.values);

  const verifyPhone = async (value: string) => {
    if (recaptchaVerifier.current) {
      try {
        if (value) {
          const confirmationResult = await signInWithPhoneNumber(firebaseAuth, value, recaptchaVerifier.current);

          confirmationResultRef.current = confirmationResult;
          setPhoneNumber(value);
          dispatch(setFormByUserType(UserLoginType.sms));
        } else {
          throw new Error('No phone number provided');
        }
      } catch (e) {
        const errorMsg = (e as Error).message;
        console.error('err', errorMsg);

        if (errorMsg.includes('auth/too-many-requests')) {
          formikContext.setFieldError('phone', translate('form.errors.authErrors.requestsAmountError'));
        }
      }
    } else {
      console.error('reCAPTCHA is undefined');
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const verifyCode = async (code: string, email: string, setFieldError: FormikHelpers<any>['setFieldError']) => {
    try {
      if (code) {
        const result = await confirmationResultRef.current?.confirm(code);
        if (isResendedCode) {
          setIsResendedCode(false);
        }

        if (result?.user?.phoneNumber) {
          // const signInResult = await dispatch(
          //   signIn({
          //     uid: result?.user.uid,
          //     email,
          //     phone: result?.user.phoneNumber,
          //     type: USER_TYPE.PREDEFINED,
          //     roomId: routeParams.roomId,
          //   }),
          // );
          // if (signInResult.meta.requestStatus === 'fulfilled') {
          //   onSuccess();
          // } else {
          //   if (typeof signInResult.payload === 'string' && signInResult.payload?.includes('Access denied')) {
          //     setFieldError('phone', translate('form.errors.authErrors.accessDenied'));
          //   }
          //   if (typeof signInResult.payload === 'string' && signInResult.payload?.includes('NO_SUCH_USER_IN_DB')) {
          //     setFieldError('phone', translate('form.errors.authErrors.noSuchUser'));
          //   }
          //   dispatch(setFormByUserType(UserLoginType.custom));
          // }
        }
      }
    } catch (e) {
      setIsIncorrectCode(true);
      console.error((e as Error).message);
    }
  };

  const resendCode = async (value: string) => {
    if (recaptchaVerifier.current) {
      try {
        if (value) {
          const confirmationResult = await signInWithPhoneNumber(firebaseAuth, value, recaptchaVerifier.current);

          confirmationResultRef.current = confirmationResult;
        } else {
          throw new Error('No phone number provided');
        }
      } catch (e) {
        console.error((e as Error).message);
      }
    } else {
      console.error('reCAPTCHA is undefined');
    }
  };

  const clickOnJustListenBtnClick = () => {
    analytics.gtag.event('audio_chat_content_click', {
      ...common,
      text_clicked: translate(`header.${userLoginType}.underButtonLabel`),
    });
    onJustListenBtnClick();
  };

  const clickOnResendBtn = () => {
    analytics.gtag.event('audio_chat_content_click', {
      ...common,
      text_clicked: translate(`header.${userLoginType}.underButtonLabel`),
    });
    setIsResendedCode(true);
    setIsIncorrectCode(false);
    resendCode(phoneNumber);
  };

  React.useEffect(() => {
    recaptchaVerifier.current = new RecaptchaVerifier(firebaseAuth, 'phone-verify', {
      size: 'invisible',
      callback: () => console.log('reCAPTCHA solved'),
    });
  }, []);

  React.useEffect(() => {
    if (userId && userName) {
      dispatch(setListenerHistory({ uid: userId, roomId: routeParams.roomId, name: userName }));
    }
  }, [userId, userName]);

  if (userLoginType === UserLoginType.none) return <></>;

  return (
    <FormikContext.Provider value={formikContext}>
      <Form
        className={cn(css.regularForm, isCustom && css.customForm, isSms && css.verifyCodeForm)}
        data-testid="login-form"
      >
        <>
          <RenderFormByUserType translate={translate} userLoginType={userLoginType} isResendedCode={isResendedCode} />
          <div className={cn(css.buttons, isSms && css.smsButtons)}>
            <Button
              className={css.submitBtn}
              label={buttonLabel}
              disabled={formikContext.isSubmitting || isButtonDisabled}
              type="submit"
            />
            {isRegular && (
              <Button
                className={css.underButton}
                label={translate(`header.${userLoginType}.underButtonLabel`)}
                isCustom
                isUnderline
                onClickHandler={clickOnJustListenBtnClick}
              />
            )}
            {userLoginType === UserLoginType.sms && (
              <p className={css.resendCode} onClick={clickOnResendBtn}>
                {translate(`header.${userLoginType}.underButtonLabel`)}
              </p>
            )}
          </div>
          <div className={css.recaptchaWrapper} id="phone-verify"></div>
        </>
      </Form>
    </FormikContext.Provider>
  );
};

export default LoginForm;
